/**
 * Broadcast / Discount Ninja drawer-cart integration.
 * Uses only Discount Ninja's native drawer-cart events and content.
 */
(function () {
  'use strict';

  var notifyQueued = false;
  var redrawQueued = false;

  /*
   * Manual discount codes that are confirmed by the merchant to be valid,
   * but cannot stack with an existing Discount Ninja product promotion.
   *
   * A code is removed only when Discount Ninja also renders it as invalid
   * and at least one cart line has an applied Discount Ninja promotion.
   */
var confirmedNonStackableCodes = {
  HELLOADELAIS: true,
  EXTRASWEET: true
};

  var rejectedCodeRemovalBusy = Object.create(null);

  function requestConfirmedCodeRemoval(code, codeKey) {
    rejectedCodeRemovalBusy[codeKey] = true;

    Promise.resolve()
      .then(function () {
        return window.discountNinja.api.discountCode.remove(code);
      })
      .then(function () {
        /*
         * Keep a short lock while Discount Ninja recalculates and removes
         * the rejected-code element from the drawer.
         */
        window.setTimeout(function () {
          delete rejectedCodeRemovalBusy[codeKey];
        }, 5000);
      })
      .catch(function () {
        /* Allow a later cart refresh to retry if the API call failed. */
        delete rejectedCodeRemovalBusy[codeKey];
      });
  }

  function removeConfirmedNonStackableRejectedCodes(drawer) {
    if (
      !drawer ||
      !window.discountNinja ||
      !window.discountNinja.api ||
      !window.discountNinja.api.discountCode ||
      typeof window.discountNinja.api.discountCode.remove !== 'function'
    ) {
      return;
    }

    var hasAppliedProductPromotion = Boolean(
      drawer.querySelector(
        '[data-la-dn-cart-item-row][data-la-dn-cart-item-isdiscounted="true"]'
      )
    );

    if (!hasAppliedProductPromotion) return;

    var rejectedCodes = drawer.querySelectorAll(
      '.limoniapps-discountninja-cartdiscountfield-discountcode' +
      '.limoniapps-discountninja-discountcode-invalid[data-discountcode]'
    );

    for (var i = 0; i < rejectedCodes.length; i++) {
      var code = (
        rejectedCodes[i].getAttribute('data-discountcode') || ''
      ).trim();
      var codeKey = code.toUpperCase();

      if (
        !codeKey ||
        !confirmedNonStackableCodes[codeKey] ||
        rejectedCodeRemovalBusy[codeKey]
      ) {
        continue;
      }

      requestConfirmedCodeRemoval(code, codeKey);
      break;
    }
  }

  function drawerExists() {
    return Boolean(document.querySelector(
      'cart-drawer[la-dn-drawercart-root], cart-drawer.limoniapps-discountninja-drawercart-root'
    ));
  }

  var discountCodeRemovalRequested = Object.create(null);
  var discountCodeRemovalInProgress = false;

  function removeAppliedDiscountCodes(drawer) {
    /*
     * Stop if the cart is no longer restricted-only.
     */
    if (
      !drawer ||
      !drawer.classList.contains('adelais-hide-discount-field') ||
      discountCodeRemovalInProgress ||
      !window.discountNinja ||
      !window.discountNinja.api ||
      !window.discountNinja.api.discountCode ||
      typeof window.discountNinja.api.discountCode.remove !== 'function'
    ) {
      return;
    }

    /*
     * Select only codes displayed inside Discount Ninja's manual
     * promotion-code field. Automatic product promotions are excluded.
     */
    var appliedCodeElements = drawer.querySelectorAll(
      '.limoniapps-discountninja-cartdiscountfield-discountcode[data-discountcode]'
    );

    var codeToRemove = '';
    var codeKey = '';

    for (var i = 0; i < appliedCodeElements.length; i++) {
      var code = (
        appliedCodeElements[i].getAttribute('data-discountcode') || ''
      ).trim();

      var key = code.toUpperCase();

      if (code && !discountCodeRemovalRequested[key]) {
        codeToRemove = code;
        codeKey = key;
        break;
      }
    }

    if (!codeToRemove) return;

    /*
     * Record the code before removing it. Discount Ninja may temporarily
     * render the same element again while recalculating, but it will not
     * be submitted repeatedly.
     */
    discountCodeRemovalRequested[codeKey] = true;
    discountCodeRemovalInProgress = true;

    Promise.resolve()
      .then(function () {
        return window.discountNinja.api.discountCode.remove(codeToRemove);
      })
      .then(function () {
        discountCodeRemovalInProgress = false;

        /*
         * Remove the next applied code sequentially, if another exists.
         */
        window.setTimeout(function () {
          removeAppliedDiscountCodes(drawer);
        }, 250);
      })
      .catch(function (error) {
        discountCodeRemovalInProgress = false;
        delete discountCodeRemovalRequested[codeKey];

        console.warn(
          'Unable to remove discount code ' + codeToRemove + ':',
          error
        );
      });
  }

  function updateDiscountFieldVisibility(drawer) {
    drawer = drawer || document.querySelector('cart-drawer');
    if (!drawer) return;

    var cartItems = drawer.querySelectorAll(
      '[data-la-dn-cart-item-row][data-item]'
    );

    var hasQualifyingItem = Boolean(
      drawer.querySelector(
        '[data-la-dn-cart-item-row][data-item][data-adelais-hide-discount-field]'
      )
    );

    var hasNormalItem = Boolean(
      drawer.querySelector(
        '[data-la-dn-cart-item-row][data-item]:not([data-adelais-hide-discount-field])'
      )
    );

    var restrictedOnly =
      cartItems.length > 0 &&
      hasQualifyingItem &&
      !hasNormalItem;

    drawer.classList.toggle(
      'adelais-hide-discount-field',
      restrictedOnly
    );

    if (restrictedOnly) {
      removeAppliedDiscountCodes(drawer);
    } else {
      /*
       * Reset after a normal product returns. Codes entered during a future
       * restricted-only transition can then be removed.
       */
      discountCodeRemovalRequested = Object.create(null);
    }
  }

  function notifyDiscountNinja() {
    if (notifyQueued) return;
    notifyQueued = true;

    // One frame is sufficient for custom elements to reconnect before
    // Discount Ninja scans the drawer markup.
    window.requestAnimationFrame(function () {
      notifyQueued = false;
      if (!drawerExists()) return;
      document.dispatchEvent(
        new CustomEvent('limoniapps:discountninja:drawercart:opened')
      );
    });
  }

  function ninjaItems(cart) {
    if (!cart) return [];
    return cart.items || cart.Items || cart.line_items || cart.LineItems || [];
  }

  function itemQuantity(item) {
    var line = item && (item.Line || item.line);
    return Number(
      (item && (item.quantity || item.Quantity)) ||
      (line && (line.quantity || line.Quantity)) ||
      0
    );
  }

  function cartMatchesDrawer(cart) {
    var items = ninjaItems(cart);
    if (!items.length) return false;

    var inputs = document.querySelectorAll(
      'cart-drawer [data-la-dn-cart-item-row] input[name="updates[]"]'
    );
    if (items.length !== inputs.length) return false;

    var ninjaQuantity = items.reduce(function (total, item) {
      return total + itemQuantity(item);
    }, 0);
    var drawerQuantity = Array.prototype.reduce.call(inputs, function (total, input) {
      return total + (parseInt(input.value, 10) || 0);
    }, 0);

    return ninjaQuantity === drawerQuantity;
  }

  /**
   * Refresh the ENTIRE drawer content (title, items, upsells, notes, totals,
   * checkout buttons) through the Section Rendering API while keeping the
   * <cart-drawer> and its sliding panel in place, so it never closes,
   * flashes or replays the slide-in animation.
   */
  function refreshCartDrawer() {
    var wrapper = document.querySelector('[data-section-type="cart-drawer"]');
    var sectionId = wrapper && wrapper.getAttribute('data-section-id');
    var drawer = wrapper && wrapper.querySelector('cart-drawer');
    var inner = drawer && drawer.querySelector('[data-cart-drawer-inner]');
    if (!sectionId || !drawer || !inner) return Promise.resolve();

    // Must NOT use /cart: the drawer section renders empty on the cart template.
    var rootUrl = (window.theme && theme.routes && theme.routes.root_url) || '/';
    return fetch(rootUrl + (rootUrl.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + sectionId, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshDrawer = doc.querySelector('[data-section-type="cart-drawer"] cart-drawer');
        var freshInner = freshDrawer && freshDrawer.querySelector('[data-cart-drawer-inner]');
        if (!freshInner) return;

        var isOpen = drawer.classList.contains('is-open');
        var scrollTop = inner.scrollTop;

        // Swap only the children of the sliding panel. The panel element and
        // <cart-drawer> stay, so no open/close animation is triggered.
        inner.innerHTML = freshInner.innerHTML;
        updateDiscountFieldVisibility(drawer);
        drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
        inner.scrollTop = scrollTop;

        // Re-initialise the panel's accordions (e.g. the PWP Deals widget).
        if (typeof inner.connectedCallback === 'function') {
          inner.collapsibles = inner.querySelectorAll('[data-collapsible]');
          inner.connectedCallback();
        }

        // Re-bind the close button, which lives inside the swapped content.
        drawer.cartDrawerClose = inner.querySelector('[data-cart-drawer-close]');
        if (drawer.cartDrawerClose) {
          drawer.cartDrawerClose.addEventListener('click', function (e) {
            e.preventDefault();
            drawer.closeCartDrawer();
          });
        }

        // Rows render at opacity 0 until the theme's is-animated class is
        // added (the theme does this in requestAnimationFrame, which never
        // runs in a background tab). Force a reflow first so the fade plays.
        void inner.offsetWidth;
        inner.querySelectorAll('[data-animation]').forEach(function (el) {
          el.classList.add('is-animated');
        });

        if (isOpen) {
          // The old <cart-items> unlocks page scroll when it disconnects.
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));

          var a11y = window.accessibility && window.accessibility.a11y;
          if (a11y && typeof a11y.trapFocus === 'function') {
            a11y.trapFocus(drawer, { elementToFocus: drawer.cartDrawerClose });
          }
        }

        // Keep the header badge in sync (cart-count listens for this).
        var count = Array.prototype.reduce.call(
          inner.querySelectorAll('input[name="updates[]"]'),
          function (total, input) { return total + (parseInt(input.value, 10) || 0); }, 0
        );
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          bubbles: true, detail: { cartCount: count }
        }));

      })
      .catch(function (error) { console.log(error); });
  }

  // Available everywhere, including the browser console.
  window.refreshCartDrawer = refreshCartDrawer;

  function redrawDrawerForDiscountNinja(event) {
    var cart = event && event.detail && event.detail.data && event.detail.data[0];

    // No redraw is needed when Discount Ninja and Broadcast already show the
    // same cart. This avoids an extra request on ordinary 1 -> 2 updates.
    if (cartMatchesDrawer(cart) || redrawQueued) return;

    redrawQueued = true;
    refreshCartDrawer().then(function () {
      redrawQueued = false;
      notifyDiscountNinja();
    });
  }

  function afterBroadcastBuild() {
    redrawQueued = false;
    notifyDiscountNinja();
  }

  // ---------------------------------------------------------------------
  // la:dn:app:enable  ==  "Discount Ninja, re-render everything you draw"
  //
  // Discount Ninja's enable handler clears its disabled flag and re-renders
  // all of its pricing / badges / promo summary. So it must run AFTER the
  // last change to the drawer content, whoever made that change:
  //   - the theme's own line-item rebuild after a +/- click,
  //   - the full drawer refresh (called by Discount Ninja's "Drawer cart
  //     refresh method" setting = window.refreshCartDrawer(), or by us),
  //   - Discount Ninja's clear/refill of gift lines, which triggers more of
  //     the above.
  //
  // Rather than guessing at event order, watch the drawer content itself:
  // every time THEME content (line items, totals) is swapped in, restart a
  // short quiet timer; when the content has stopped changing, fire once.
  // Discount Ninja's own DOM writes (badges, strikethrough prices, summary)
  // never contain those theme nodes, so they do not restart the timer and
  // cannot cause a loop.
  // ---------------------------------------------------------------------
  var ENABLE_QUIET_MS = 400;   // content must be stable this long before firing
  var ENABLE_LATE_MS = 2500;   // one late re-call: Discount Ninja's own pricing pass
                               // runs ~1.7s after its refresh; this redraws after it
  var enableTimer = null;
  var enableLateTimer = null;
  var THEME_CONTENT = '[data-item], input[name="updates[]"], [data-cart-total], [data-cart-empty], cart-items';

  function fireEnable() {
    document.dispatchEvent(new CustomEvent('la:dn:app:enable'));
  }

  function scheduleEnable() {
    window.clearTimeout(enableTimer);
    window.clearTimeout(enableLateTimer);
    enableTimer = window.setTimeout(fireEnable, ENABLE_QUIET_MS);
    enableLateTimer = window.setTimeout(fireEnable, ENABLE_LATE_MS);
  }

  function nodeHasThemeContent(node) {
    if (node.nodeType !== 1) return false;
    return node.matches(THEME_CONTENT) || Boolean(node.querySelector(THEME_CONTENT));
  }

  function isThemeContentChange(mutation) {
    if (mutation.type !== 'childList') return false;
    var i;
    for (i = 0; i < mutation.addedNodes.length; i++) {
      if (nodeHasThemeContent(mutation.addedNodes[i])) return true;
    }
    for (i = 0; i < mutation.removedNodes.length; i++) {
      if (nodeHasThemeContent(mutation.removedNodes[i])) return true;
    }
    return false;
  }

  function watchDrawerContent() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__laDnWatched) return;
    drawer.__laDnWatched = true;

    var observer = new MutationObserver(function (mutations) {
      updateDiscountFieldVisibility(drawer);
      removeConfirmedNonStackableRejectedCodes(drawer);

      // Only run Discount Ninja refresh logic while the drawer is open.
      if (!drawer.classList.contains('is-open')) return;
      for (var i = 0; i < mutations.length; i++) {
        if (isThemeContentChange(mutations[i])) { scheduleEnable(); return; }
      }
    });
    observer.observe(drawer, { childList: true, subtree: true });
    updateDiscountFieldVisibility(drawer);
    removeConfirmedNonStackableRejectedCodes(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawerContent);
  } else {
    watchDrawerContent();
  }

  // Opening the drawer also re-renders Discount Ninja's content (covers
  // changes made while it was closed, e.g. add-to-cart from a product page).
  document.addEventListener('theme:cart-drawer:open', function () {
    var drawer = document.querySelector('cart-drawer');
    updateDiscountFieldVisibility(drawer);
    removeConfirmedNonStackableRejectedCodes(drawer);
    scheduleEnable();
  });

  document.addEventListener('theme:product:added', afterBroadcastBuild);
  document.addEventListener('theme:cart-drawer:open', notifyDiscountNinja);

  // ---------------------------------------------------------------------
  // la:dn:collection:updated  ==  "Discount Ninja, re-price the upsell
  // (PWP) products". Discount Ninja does not pick these up by itself when
  // the drawer opens, so we fire the event from every signal that can
  // change what the shopper is looking at:
  //   - drawer loaded on the page (server-rendered upsell)
  //   - drawer opens (cart icon, or add-to-cart)
  //   - a product is added while the drawer is already open
  //   - the upsell list is re-rendered by the theme's cart rebuild
  //   - Discount Ninja finishes its own drawer refresh / pricing pass
  //   - the shopper scrolls or taps inside the upsell carousel
  // All of them go through one debounced dispatcher so bursts collapse to
  // a single event, plus one late re-fire after Discount Ninja's own pass.
  // ---------------------------------------------------------------------
  var COLLECTION_DEBOUNCE_MS = 300;    // quiet time before firing (scroll/rebuild)
  var COLLECTION_OPEN_DELAY_MS = 3000; // settle time after open / add / page load
  var COLLECTION_LATE_MS = 5000;       // late re-fire after DN's own pricing pass
  var collectionTimer = null;
  var collectionLateTimer = null;
  var collectionLastReason = '';

  function fireCollectionUpdated(reason) {
    document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
    console.log('[Adelais] la:dn:collection:updated dispatched (' + reason + ')');
  }

  function scheduleCollectionUpdated(reason, delay) {
    collectionLastReason = reason;
    window.clearTimeout(collectionTimer);
    window.clearTimeout(collectionLateTimer);
    collectionTimer = window.setTimeout(function () {
      fireCollectionUpdated(collectionLastReason);
    }, delay || COLLECTION_DEBOUNCE_MS);
    collectionLateTimer = window.setTimeout(function () {
      fireCollectionUpdated(collectionLastReason + ', late pass');
    }, COLLECTION_LATE_MS);
  }

  // 1. Drawer opened / product added.
  document.addEventListener('theme:cart-drawer:open', function () {
    scheduleCollectionUpdated('drawer opened', COLLECTION_OPEN_DELAY_MS);
  });
  document.addEventListener('theme:product:added', function () {
    scheduleCollectionUpdated('product added', COLLECTION_OPEN_DELAY_MS);
  });

  // 2. Discount Ninja finished its own refresh.
  document.addEventListener('limoniapps:discountninja:drawercart:refreshed', function () {
    scheduleCollectionUpdated('discount ninja refreshed');
  });

  // 3. Upsell list re-rendered, 4. carousel scrolled / tapped, 5. page load.
  function isUpsellNode(node) {
    return node.nodeType === 1 && (
      node.matches('[data-upsell-products], [data-quick-add-holder], quick-add-product') ||
      Boolean(node.querySelector('[data-quick-add-holder]'))
    ) && Boolean(node.closest('[data-upsell-products], [data-upsell-widget]'));
  }

  function watchUpsellProducts() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__laDnUpsellWatched) return;
    drawer.__laDnUpsellWatched = true;

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (isUpsellNode(added[j])) {
            scheduleCollectionUpdated('upsell re-rendered');
            return;
          }
        }
      }
    }).observe(drawer, { childList: true, subtree: true });

    // Scroll events do not bubble, so listen in the capture phase on the
    // drawer and only react to scrolling inside the upsell widget.
    var scrollTick = false;
    drawer.addEventListener('scroll', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      var target = e.target;
      if (!target || target.nodeType !== 1) return;
      if (!target.closest('[data-upsell-widget], [data-upsell-products]')) return;
      if (scrollTick) return;
      scrollTick = true;
      window.setTimeout(function () { scrollTick = false; }, 250);
      scheduleCollectionUpdated('upsell scrolled');
    }, true);

    // Taps/clicks inside the upsell (e.g. carousel arrows, accordion toggle).
    drawer.addEventListener('click', function (e) {
      var target = e.target;
      if (target && target.nodeType === 1 &&
          target.closest('[data-upsell-widget], [data-upsell-products]')) {
        scheduleCollectionUpdated('upsell clicked');
      }
    }, true);

    // Drawer (and its server-rendered upsell) is already in the page.
    scheduleCollectionUpdated('drawer loaded', COLLECTION_OPEN_DELAY_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchUpsellProducts);
  } else {
    watchUpsellProducts();
  }

  document.addEventListener(
    'limoniapps:discountninja:drawercart:refreshed',
    redrawDrawerForDiscountNinja
  );
})();
